import * as roomApi from '@/api/hotel/room'
import * as facilityApi from '@/api/hotel/facility'
import * as poiApi from '@/api/hotel/poi'

/**
 * 关联表名称缓存 (进程内 Map 缓存)
 * 用于把列表里的 shopId/spuId/roomId/facilityId/poiCategoryId 等转成可读名称
 * 注意：ID 全程使用字符串，避免 JS Number 精度丢失（雪花ID 19位超出安全整数范围）
 */
const cache = new Map<string, Map<string, string>>()

/**
 * 通用 getOrFetch：根据 ids 批量补全 name，返回新的 Map
 */
async function batchFill(
  key: string,
  ids: Array<string | number | null | undefined>,
  fetcher: (id: string) => Promise<string | null>
): Promise<Map<string, string>> {
  const sub = cache.get(key) || new Map<string, string>()
  cache.set(key, sub)

  // 过滤掉空值、已经在缓存里的
  const missing: string[] = []
  for (const raw of ids) {
    if (raw === null || raw === undefined) continue
    const id = String(raw)
    if (!id || sub.has(id)) continue
    missing.push(id)
  }
  if (missing.length === 0) return sub

  // 并发拉取
  const results = await Promise.allSettled(
    missing.map(async (id) => {
      const name = await fetcher(id)
      return { id, name }
    })
  )
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value.name) {
      sub.set(r.value.id, r.value.name)
    }
  }
  return sub
}

export const hotelLookup = {
  /**
   * 房间房号: roomId -> roomNo
   */
  async fillRoomNos(roomIds: Array<number | string | null | undefined>): Promise<Map<string, string>> {
    return batchFill('roomNo', roomIds, async (id) => {
      const data: any = await roomApi.getRoom(id)
      return data?.roomNo || null
    })
  },

  /**
   * 设施名: facilityId -> facilityName
   */
  async fillFacilityNames(facilityIds: Array<number | null | undefined>): Promise<Map<string, string>> {
    return batchFill('facilityName', facilityIds, async (id) => {
      const data: any = await facilityApi.getFacility(id)
      return data?.name || null
    })
  },

  /**
   * POI 分类名: poiCategoryId -> categoryName
   */
  async fillPoiCategoryNames(categoryIds: Array<number | null | undefined>): Promise<Map<string, string>> {
    return batchFill('poiCategoryName', categoryIds, async (id) => {
      const data: any = await poiApi.getPoiCategory(id)
      return data?.name || null
    })
  },

  /**
   * 通用: 给列表行打补丁，传入目标字段和新字段名
   * @param list 后端返回的列表
   * @param idField 列表行中的 ID 字段名 (如 'roomId')
   * @param nameField 注入到行里的字段名 (如 'roomNo')
   * @param lookup 上面 fillXxx 返回的 Map
   */
  patchList<T extends Record<string, any>>(
    list: T[],
    idField: keyof T,
    nameField: string,
    lookup: Map<string, string>
  ): T[] {
    for (const row of list) {
      const id = String(row[idField])
      const name = lookup.get(id)
      if (name) row[nameField] = name
    }
    return list
  },

  getCache() { return cache },
  clearCache() { cache.clear() }
}
